import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  SimpleGrid,
  Badge,
  Icon,
  List,
  ListItem,
  ListIcon,
  Image,
  Input,
  Link,
  Progress,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { 
  FaDumbbell, 
  FaGolfBall,
  FaSwimmer,
  FaTableTennis,
  FaYinYang,
  FaStore,
  FaBuilding,
  FaRegBuilding,
} from 'react-icons/fa';
import { GiTennisRacket, GiMuscleUp } from 'react-icons/gi';
import { MdSportsScore } from 'react-icons/md';
import Logo from './Logo';

const businessTypes = [
  { name: 'Gym', icon: FaDumbbell },
  { name: 'Golf', icon: FaGolfBall },
  { name: 'Tennis', icon: GiTennisRacket },
  { name: 'Swimming', icon: FaSwimmer },
  { name: 'Yoga', icon: FaYinYang },
  { name: 'Pilates', icon: GiMuscleUp },
  { name: 'Pickleball', icon: FaTableTennis },
  { name: 'Other', icon: MdSportsScore },
];

const locationTypes = [
  { 
    name: 'New', 
    icon: FaStore,
    description: '(about to open our first location)'
  },
  { 
    name: 'Single location', 
    icon: FaRegBuilding,
    description: '(1 location)'
  },
  { 
    name: 'Multi location', 
    icon: FaBuilding,
    description: '(2+ locations)'
  },
];

const HeroSection = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    businessType: '',
    locationType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const handleBusinessSelect = (businessType) => {
    setFormData({ ...formData, businessType });
    setStep(2);
  };

  const handleLocationSelect = (locationType) => {
    setFormData({ ...formData, locationType });
    setStep(3);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Add your form submission logic here
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Text color="gray.400" textAlign="center" fontSize={{ base: 'md', md: 'lg' }}>
              What type of business do you have?
            </Text>
            <SimpleGrid 
              columns={{ base: 2, md: 4 }} 
              spacing={{ base: 4, md: 4 }}
              px={{ base: 2, md: 0 }}
            >
              {businessTypes.map((type) => (
                <Button
                  key={type.name}
                  variant="outline"
                  size="lg"
                  height={{ base: "120px", md: "100px" }}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="gray.700"
                  _hover={{ bg: '#1A1824', borderColor: '#82ca9c' }}
                  onClick={() => handleBusinessSelect(type.name)}
                  display="flex"
                  flexDirection="column"
                  gap={{ base: 4, md: 2 }}
                  bg="#110F14"
                  color="white"
                  py={{ base: 6, md: 4 }}
                >
                  <Icon 
                    as={type.icon} 
                    boxSize={{ base: 8, md: 6 }} 
                    color="#82ca9c" 
                  />
                  <Text 
                    fontSize={{ base: "md", md: "sm" }}
                    fontWeight="medium"
                  >
                    {type.name}
                  </Text>
                </Button>
              ))}
            </SimpleGrid>
          </>
        );

      case 2:
        return (
          <>
            <Text color="gray.400" textAlign="center" fontSize={{ base: 'md', md: 'lg' }} mb={6}>
              How many locations do you have?
            </Text>
            <VStack 
              spacing={4}
              width="100%"
              mb={6}
            >
              {locationTypes.map((type) => (
                <Button
                  key={type.name}
                  variant="outline"
                  width="100%"
                  height={{ base: "80px", md: "100px" }}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor="gray.700"
                  _hover={{ bg: '#1A1824', borderColor: '#82ca9c' }}
                  onClick={() => handleLocationSelect(type.name)}
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  px={6}
                  bg="#110F14"
                  color="white"
                  position="relative"
                >
                  <Icon 
                    as={type.icon} 
                    boxSize={{ base: 6, md: 7 }} 
                    color="#82ca9c"
                    mr={4}
                  />
                  <VStack 
                    align="flex-start" 
                    spacing={0}
                  >
                    <Text 
                      fontSize={{ base: "md", md: "lg" }} 
                      fontWeight="semibold"
                    >
                      {type.name}
                    </Text>
                    <Text 
                      fontSize={{ base: "sm", md: "md" }} 
                      color="gray.400"
                    >
                      {type.description}
                    </Text>
                  </VStack>
                </Button>
              ))}
            </VStack>
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              borderColor="gray.700"
              color="gray.400"
              _hover={{ bg: '#1A1824' }}
              bg="#110F14"
              size={{ base: "md", md: "lg" }}
              width={{ base: "100%", md: "auto" }}
            >
              Previous
            </Button>
          </>
        );

      case 3:
        return (
          <VStack spacing={4} as="form" onSubmit={handleSubmit}>
            <SimpleGrid 
              columns={{ base: 1, sm: 2 }} 
              spacing={4} 
              w="100%"
            >
              <Box>
                <Text mb={2} color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>First name</Text>
                <Input
                  name="firstName"
                  placeholder="Your first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  borderColor="gray.700"
                  bg="#110F14"
                  color="white"
                  _hover={{ borderColor: '#82ca9c' }}
                  _focus={{ borderColor: '#82ca9c', boxShadow: 'none' }}
                  _placeholder={{ color: 'gray.500' }}
                  size={{ base: "md", md: "lg" }}
                />
              </Box>
              <Box>
                <Text mb={2} color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>Last name</Text>
                <Input
                  name="lastName"
                  placeholder="Your last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  borderColor="gray.700"
                  bg="#110F14"
                  color="white"
                  _hover={{ borderColor: '#82ca9c' }}
                  _focus={{ borderColor: '#82ca9c', boxShadow: 'none' }}
                  _placeholder={{ color: 'gray.500' }}
                  size={{ base: "md", md: "lg" }}
                />
              </Box>
            </SimpleGrid>
            <SimpleGrid 
              columns={{ base: 1, sm: 2 }} 
              spacing={4} 
              w="100%"
            >
              <Box>
                <Text mb={2} color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>Email</Text>
                <Input
                  name="email"
                  type="email"
                  placeholder="example@acme.co"
                  value={formData.email}
                  onChange={handleInputChange}
                  borderColor="gray.700"
                  bg="#110F14"
                  color="white"
                  _hover={{ borderColor: '#82ca9c' }}
                  _focus={{ borderColor: '#82ca9c', boxShadow: 'none' }}
                  _placeholder={{ color: 'gray.500' }}
                  size={{ base: "md", md: "lg" }}
                />
              </Box>
              <Box>
                <Text mb={2} color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>Phone number</Text>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="+1 555 5555"
                  value={formData.phone}
                  onChange={handleInputChange}
                  borderColor="gray.700"
                  bg="#110F14"
                  color="white"
                  _hover={{ borderColor: '#82ca9c' }}
                  _focus={{ borderColor: '#82ca9c', boxShadow: 'none' }}
                  _placeholder={{ color: 'gray.500' }}
                  size={{ base: "md", md: "lg" }}
                />
              </Box>
            </SimpleGrid>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.400" mt={4}>
              By submitting this form, I confirm that I have read and understood BirdieHouse's{' '}
              <Link color="#82ca9c" href="/privacy-policy">
                privacy policy
              </Link>
            </Text>
            <HStack spacing={4} w="100%">
              <Button 
                variant="outline" 
                onClick={handlePrevious} 
                flex={1}
                borderColor="gray.700"
                color="gray.400"
                _hover={{ bg: '#1A1824' }}
                bg="#110F14"
                size={{ base: "md", md: "lg" }}
              >
                Previous
              </Button>
              <Button 
                type="submit" 
                flex={1}
                bg="#82ca9c"
                color="white"
                _hover={{ bg: '#6eb589' }}
                size={{ base: "md", md: "lg" }}
              >
                Book Your Demo
              </Button>
            </HStack>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Box py={{ base: 8, md: 20 }} bg="#110F14">
      <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
        <Box mb={{ base: 8, md: 16 }}>
          <HStack 
            justify="space-between" 
            align="center"
            flexDir={{ base: 'column', md: 'row' }}
            spacing={{ base: 6, md: 0 }}
          >
            <Logo variant={4} size={{ base: "regular", md: "large" }} />
            <HStack 
              spacing={6} 
              display={{ base: 'none', md: 'flex' }}
            >
              <Button
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
              >
                Features
              </Button>
              <Button
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
              >
                Pricing
              </Button>
              <Button
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
              >
                FAQ
              </Button>
            </HStack>
          </HStack>
        </Box>
        
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: 8, lg: 12 }}
          align={{ base: 'stretch', lg: 'center' }}
          justify="space-between"
        >
          <VStack 
            spacing={{ base: 6, md: 8 }} 
            align="stretch" 
            maxW="600px"
            order={{ base: 2, lg: 1 }}
          >
            <VStack spacing={{ base: 4, md: 6 }} align="stretch">
              <Badge
                colorScheme="green"
                bg="#82ca9c"
                color="white"
                px={4}
                py={2}
                borderRadius="full"
                fontSize={{ base: 'sm', md: 'md' }}
                alignSelf="flex-start"
              >
                Get a Free Demo
              </Badge>
              
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                color="white"
                lineHeight="1.2"
                letterSpacing="tight"
                fontWeight="bold"
              >
                Grow your sports business with BirdieHouse
              </Heading>
              
              <Text 
                fontSize={{ base: 'lg', md: 'xl' }} 
                color="gray.400" 
                maxW="2xl"
              >
                Transform your sports business with our all-in-one management platform
              </Text>
            </VStack>

            <List spacing={{ base: 4, md: 5 }}>
              <ListItem color="gray.400" fontSize={{ base: 'md', md: 'lg' }}>
                <HStack align="start" spacing={4}>
                  <ListIcon as={CheckIcon} color="#82ca9c" mt={1.5} boxSize={5} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" color="white">
                      Streamline Operations
                    </Text>
                    <Text>Automate bookings and member management with our intuitive platform</Text>
                  </VStack>
                </HStack>
              </ListItem>
              <ListItem color="gray.400" fontSize={{ base: 'md', md: 'lg' }}>
                <HStack align="start" spacing={4}>
                  <ListIcon as={CheckIcon} color="#82ca9c" mt={1.5} boxSize={5} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" color="white">
                      Boost Revenue
                    </Text>
                    <Text>Implement smart pricing and packages to maximize your earnings</Text>
                  </VStack>
                </HStack>
              </ListItem>
              <ListItem color="gray.400" fontSize={{ base: 'md', md: 'lg' }}>
                <HStack align="start" spacing={4}>
                  <ListIcon as={CheckIcon} color="#82ca9c" mt={1.5} boxSize={5} />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="semibold" color="white">
                      Reduce Costs
                    </Text>
                    <Text>Cut operational expenses with automated systems and analytics</Text>
                  </VStack>
                </HStack>
              </ListItem>
            </List>
          </VStack>

          <Box
            w={{ base: 'full', lg: '500px' }}
            bg="#1A1824"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="2xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            order={{ base: 1, lg: 2 }}
          >
            <VStack spacing={{ base: 6, md: 8 }}>
              <VStack spacing={2} w="full">
                <Text 
                  color="white" 
                  fontSize={{ base: 'xl', md: '2xl' }} 
                  fontWeight="bold"
                >
                  Get Started Today
                </Text>
                <Text color="gray.400">
                  Provide some basic info about your business to book your free demo
                </Text>
              </VStack>
              
              <Progress
                value={(step / 3) * 100}
                size="sm"
                colorScheme="green"
                bg="gray.700"
                w="100%"
                borderRadius="full"
              />
              
              {renderStepContent()}
            </VStack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection; 